import React from 'react';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { getStoreCurrencyFormatter } from 'utils';
import { useStore } from 'context/store-context';
import { ChartPieIcon } from '@heroicons/react/outline';

type PercentageModalProps = {
  show: boolean;
  setShowModal: (value: boolean) => void;
  isLoading?: boolean;
  handleSetRule?: (type: string) => void;
  percentageInput: {
    maximumFee: string;
    minimumFee: string;
    percentage: string;
  };
  setInput: (field: string, value: string) => void;
  isDisabled?: boolean;
};

export const PercentageModal = ({
  show,
  setShowModal,
  isLoading,
  handleSetRule,
  percentageInput,
  setInput,
  isDisabled,
}: PercentageModalProps) => {
  const { storeProperties } = useStore();

  const handleFieldUpdate = (key: string, value: string) => {
    if (isDisabled) return;
    const num = Number(value);
    if (isNaN(num)) {
      return;
    }
    setInput(key, value);
  };

  return (
    <Modal
      openModal={(value) => setShowModal(value)}
      open={show}
      containerClass="w-full max-w-2xl !p-0 overflow-hidden"
      iconClassName="text-[#64748b] hover:text-[#0b1c30]"
    >
      <div className="px-6 pb-6 pt-1">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
            <ChartPieIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0b1c30]">
              Percentage Guarantee Fee
            </h2>
            <p className="mt-1 text-sm leading-6 text-[#64748b]">
              Calculate the guarantee fee from the order value, with optional
              minimum and maximum fee limits.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-[#d8dfeb] bg-[#f8fbff] p-4">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#737686]">
              Minimum fee
            </label>
            <input
              name="fee"
              className="w-full rounded-lg border border-[#d8dfeb] bg-white px-4 py-3 text-sm font-semibold text-[#0b1c30] shadow-sm focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] disabled:bg-slate-100 disabled:text-slate-400"
              placeholder={`e.g ${getStoreCurrencyFormatter(
                storeProperties?.currency,
                0,
              )}`}
              value={percentageInput.minimumFee}
              onChange={(e) =>
                handleFieldUpdate(
                  'minimumFee',
                  e.target.value.replace(/[^0-9.]/g, ''),
                )
              }
              disabled={isDisabled}
            />
          </div>
          <div className="rounded-xl border border-[#d8dfeb] bg-[#f8fbff] p-4">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#737686]">
              Maximum fee
            </label>
            <input
              required
              name="maximumFee"
              className="w-full rounded-lg border border-[#d8dfeb] bg-white px-4 py-3 text-sm font-semibold text-[#0b1c30] shadow-sm focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] disabled:bg-slate-100 disabled:text-slate-400"
              placeholder={`e.g ${getStoreCurrencyFormatter(
                storeProperties?.currency,
                10000,
              )}`}
              value={percentageInput.maximumFee}
              onChange={(e) =>
                handleFieldUpdate(
                  'maximumFee',
                  e.target.value.replace(/[^0-9.]/g, ''),
                )
              }
              disabled={isDisabled}
            />
          </div>
          <div className="rounded-xl border border-[#d8dfeb] bg-[#f8fbff] p-4 sm:col-span-2">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#737686]">
              Percent of cart value
            </label>
            <div className="relative">
              <input
                name="percentage"
                className="w-full rounded-lg border border-[#d8dfeb] bg-white px-4 py-3 pr-10 text-sm font-semibold text-[#0b1c30] shadow-sm focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] disabled:bg-slate-100 disabled:text-slate-400"
                placeholder="e.g 3"
                value={percentageInput.percentage}
                onChange={(e) =>
                  handleFieldUpdate(
                    'percentage',
                    e.target.value.replace(/[^0-9.]/g, ''),
                  )
                }
                disabled={isDisabled}
              />
              <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm font-bold text-[#737686]">
                %
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-[#e5ebf4] bg-[#f8fbff] px-6 py-4">
        <button
          type="button"
          onClick={() => setShowModal(false)}
          className="rounded-lg border border-[#d8dfeb] bg-white px-4 py-2 text-sm font-semibold text-[#334155] shadow-sm transition hover:bg-[#eff4ff]"
        >
          Cancel
        </button>
        <Button
          onClick={() => handleSetRule && handleSetRule('percentage')}
          className="rounded-lg bg-[#2563eb] px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1d4ed8]"
          isLoading={isLoading}
          disabled={isDisabled}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};
